import { ReactNode, useMemo } from 'react';
import ForumIcon from '@mui/icons-material/Forum';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ClassIcon from '@mui/icons-material/Class';
import SummarizeIcon from '@mui/icons-material/Summarize';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { useCourseCore } from '../../core/modules/course';
import { useNavigationCore } from '../../core/router/navigation';
import {
  CLUB_DE_TRADING_HUB_URL,
  MARATON_DE_INVERSIONES_PORTAFOLIOS_URL,
  isClubDeTradingCourse,
  isMaratonDeInversionesCourse,
} from '../../core/constants/courses';

export interface CourseSection {
  key: 'classes' | 'calendar' | 'reports' | 'forum' | 'alliances';
  label: string;
  icon: ReactNode;
  tour: string;
  onClick: () => void;
  // Para marcar la sección activa según la URL
  path: RegExp;
}

export interface CourseTool {
  label: string;
  onClick: () => void;
}

const openExternal = (url: string) => window.open(url, '_blank', 'noopener,noreferrer');

/** Secciones y herramientas del curso actual. Las usan la barra de arriba y las pestañas del celular. */
const useCourseSections = () => {
  const { course, currentClassId } = useCourseCore();
  const {
    goToPositionCalculator, goToCalendar, goToForum, goToCourseReports, goToMeCourse, goToAlliance,
    goToDiagnosticQuestions, goToTradingMetrics, goToLiquidAssets, goToRealEstate, goToFinancialGoals, goToFinancialMetrics,
  } = useNavigationCore() as any;

  const sections = useMemo<CourseSection[]>(() => {
    if (!course) {
      return [];
    }
    const id = course.id;
    const list: (CourseSection | false)[] = [
      { key: 'classes', label: 'Clases', icon: <ClassIcon />, tour: 'classes', onClick: () => goToMeCourse(id, `${currentClassId}`), path: /\/me\/courses?\/[^/]+(\/classes)?$/ },
      course.calendar && { key: 'calendar', label: 'Calendario', icon: <CalendarMonthIcon />, tour: 'calendar', onClick: () => goToCalendar(id), path: /\/calendar$/ },
      course.report && { key: 'reports', label: 'Reportes', icon: <SummarizeIcon />, tour: 'reports', onClick: () => goToCourseReports(id), path: /\/reports$/ },
      course.forum && { key: 'forum', label: 'Foro', icon: <ForumIcon />, tour: 'forum', onClick: () => goToForum(id), path: /\/forum$/ },
      course.alliance && { key: 'alliances', label: 'Alianzas', icon: <HandshakeIcon />, tour: 'alliances', onClick: () => goToAlliance(id), path: /\/alliance$/ },
    ];
    return list.filter(Boolean) as CourseSection[];
  }, [course, currentClassId, goToAlliance, goToCalendar, goToCourseReports, goToForum, goToMeCourse]);

  const tools = useMemo<CourseTool[]>(() => {
    if (!course) {
      return [];
    }
    const id = course.id;
    const c = course as any;
    const list: (CourseTool | false)[] = [
      isMaratonDeInversionesCourse(course) && { label: 'Portafolios', onClick: () => openExternal(MARATON_DE_INVERSIONES_PORTAFOLIOS_URL) },
      isClubDeTradingCourse(course) && { label: 'Club de Trading HUB', onClick: () => openExternal(CLUB_DE_TRADING_HUB_URL) },
      course.positionCalculator && { label: 'Calculadora de Posición', onClick: () => goToPositionCalculator(id) },
      course.diagnosticAnswers && { label: 'Preguntas Diagnóstico', onClick: () => goToDiagnosticQuestions(id) },
      c.financialMetrics && { label: 'Métricas Financieras', onClick: () => goToFinancialMetrics(id) },
      course.tradingMetrics && { label: 'Métricas Trading', onClick: () => goToTradingMetrics(id) },
      c.liquidAssets && { label: 'Activos Líquidos', onClick: () => goToLiquidAssets(id) },
      c.realEstate && { label: 'Departamentos', onClick: () => goToRealEstate(id) },
      c.financialGoals && { label: 'Metas Financieras', onClick: () => goToFinancialGoals(id) },
    ];
    return list.filter(Boolean) as CourseTool[];
  }, [course, goToDiagnosticQuestions, goToFinancialGoals, goToFinancialMetrics, goToLiquidAssets, goToPositionCalculator, goToRealEstate, goToTradingMetrics]);

  return { course, sections, tools };
};

export default useCourseSections;
